import type React from 'react';
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  BadgeCheck, MapPin, Calendar, ExternalLink, Globe, Building2,
  ChevronDown, ChevronUp
} from 'lucide-react';
import venueApi from '@showliner/shared/services/venueApi';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { getAssetUrl } from '@/utils/crossAppLinks';
import { renderSocialIcon, getPlatformColor, type SocialPlatformKey, getPlatformLabel, type SocialLinkEntry, getVisibleSocialLinks } from '@showliner/shared/utils/socialMedia';
import type { ProfileLink, UpcomingEventsLink } from '@showliner/shared/types/profileLinks';
import { usePageTitle } from '@/hooks/usePageTitle';
import ProfileContact, { type PublicContact } from '@/components/ProfileContact';
import PlayedTogether from '@/components/PlayedTogether';
import { browserTimeZone, formatListedShowTime } from '@showliner/shared/utils/showTimezone';

interface VenueData {
  /** Published contact (email/phone), or null. Never the direct tier. */
  publicContact?: PublicContact | null;
  id: string;
  name: string;
  isVerified?: boolean;
  description?: string | null;
  profilePicture?: string | null;
  socialLinks?: SocialLinkEntry[] | null;
  customLinks?: ProfileLink[] | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  capacity?: number | null;
  upcomingEvents?: Array<{
    id: string;
    name: string;
    startTime: string;
    /** The show's IANA zone (#863); null when none is on record. */
    timezone?: string | null;
    performerNames?: string[];
    /** A stage of a public festival: the row opens the festival's page. */
    festival?: { id: string; name: string } | null;
  }>;
}

// --- Social icon link ---

const SocialIconLink: React.FC<{ platform: string; url: string }> = ({ platform, url }) => {
  const platformKey = platform.toLowerCase() as SocialPlatformKey;
  const icon = renderSocialIcon(platformKey, { size: 20 });
  const color = getPlatformColor(platformKey);

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={getPlatformLabel(platform)}
      className="inline-flex items-center justify-center h-10 w-10 rounded-lg border border-border/50 hover:scale-110 transition-all duration-200"
      style={{ color }}
    >
      {icon || <Globe className="h-5 w-5" />}
    </a>
  );
};

// --- Upcoming Events block ---

const UpcomingEventsBlock: React.FC<{
  link: UpcomingEventsLink;
  events?: VenueData['upcomingEvents'];
}> = ({ link, events }) => {
  const [expanded, setExpanded] = useState(false);

  if (!events || events.length === 0) return null;

  const displayMode = link.displayMode || 'next_three';
  // The visitor's zone, only to decide whether to name the show's (#863).
  const viewerZone = browserTimeZone();
  let visibleEvents = events;
  let canExpand = false;

  if (displayMode === 'next' && !expanded) {
    visibleEvents = events.slice(0, 1);
    canExpand = events.length > 1;
  } else if (displayMode === 'next_three' && !expanded) {
    visibleEvents = events.slice(0, 3);
    canExpand = events.length > 3;
  }

  if (expanded) {
    visibleEvents = events;
  }

  return (
    <div className="w-full rounded-lg border border-border/50 overflow-hidden">
      <div className="px-4 py-3 bg-muted/30 border-b border-border/50">
        <h3 className="text-sm font-semibold text-grey-900">{link.title || 'Upcoming events'}</h3>
      </div>
      <div className="divide-y divide-border/30">
        {visibleEvents.map((event) => (
          <div key={event.id} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/20 transition-colors">
            <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              {event.festival ? (
                <Link to={`/festival/${event.festival.id}`} className="block text-sm font-medium text-grey-900 truncate hover:text-primary">
                  {event.name}
                </Link>
              ) : (
                <p className="text-sm font-medium text-grey-900 truncate">{event.name}</p>
              )}
              <p className="text-xs text-muted-foreground">
                {formatListedShowTime(event.startTime, event.timezone, viewerZone)}
              </p>
            </div>
          </div>
        ))}
      </div>
      {canExpand && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="w-full px-4 py-2.5 text-xs font-medium text-primary hover:bg-muted/20 transition-colors flex items-center justify-center gap-1"
        >
          {expanded ? (
            <><ChevronUp className="h-3.5 w-3.5" /> Show less</>
          ) : (
            <><ChevronDown className="h-3.5 w-3.5" /> Show all {events.length} events</>
          )}
        </button>
      )}
    </div>
  );
};

// --- Profile link button ---

const ProfileLinkButton: React.FC<{ title: string; url: string }> = ({ title, url }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className="w-full flex items-center justify-between px-5 py-3.5 rounded-lg border border-border/50 bg-white hover:border-primary/30 hover:shadow-sm transition-all duration-200 group"
  >
    <span className="text-sm font-medium text-grey-900">{title}</span>
    <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
  </a>
);

// --- Loading skeleton ---

const ProfileSkeleton: React.FC = () => (
  <div className="flex flex-col items-center px-4 pt-12 pb-8 max-w-lg mx-auto">
    <Skeleton className="h-28 w-28 rounded-xl" />
    <Skeleton className="h-7 w-48 mt-4" />
    <Skeleton className="h-4 w-64 mt-2" />
    <div className="flex gap-3 mt-6">
      {[1, 2, 3].map(i => <Skeleton key={i} className="h-10 w-10 rounded-lg" />)}
    </div>
    <div className="w-full space-y-3 mt-8">
      {[1, 2, 3].map(i => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
    </div>
  </div>
);

// --- Main component ---

const VenueProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [venue, setVenue] = useState<VenueData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  usePageTitle(venue?.name);

  useEffect(() => {
    const fetchVenue = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const data = await venueApi.getVenue(id);
        setVenue(data as unknown as VenueData);
      } catch {
        setError('Couldn\'t load this venue.');
      } finally {
        setLoading(false);
      }
    };

    fetchVenue();
  }, [id]);

  if (loading) return <ProfileSkeleton />;

  if (error || !venue) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Building2 className="h-16 w-16 text-muted-foreground/50" />
        <p className="text-lg text-muted-foreground">{error || 'Venue not found'}</p>
        <Button variant="outline" asChild>
          <Link to="/discover">Browse venues</Link>
        </Button>
      </div>
    );
  }

  const visibleSocialLinks = getVisibleSocialLinks(venue.socialLinks || []);
  const hasSocialLinks = visibleSocialLinks.length > 0;
  const hasLocation = venue.address || venue.city;
  const customLinks = (venue.customLinks || []).filter(l => l.visible !== false).sort((a, b) => a.position - b.position);

  return (
    <div className="flex flex-col items-center px-4 pt-12 pb-16 max-w-lg mx-auto">
      {/* Avatar (rounded square for venues) */}
      <Avatar className="h-28 w-28 rounded-xl shadow-lg">
        {venue.profilePicture ? (
          <AvatarImage src={getAssetUrl(venue.profilePicture)} alt={venue.name} className="rounded-xl" />
        ) : null}
        <AvatarFallback className="text-3xl font-bold bg-primary text-white rounded-xl">
          {venue.name.charAt(0).toUpperCase()}
        </AvatarFallback>
      </Avatar>

      {/* Name + Badge */}
      <div className="flex items-center gap-2 mt-4">
        <h1 className="font-display text-2xl font-bold tracking-tight text-grey-900">{venue.name}</h1>
        {venue.isVerified && <BadgeCheck className="h-6 w-6 text-verified" />}
      </div>

      {/* Location */}
      {hasLocation && (
        <p className="flex items-center gap-1 text-sm text-muted-foreground mt-1.5">
          <MapPin className="h-4 w-4" />
          {[venue.address, venue.city, venue.state].filter(Boolean).join(', ')}
        </p>
      )}
      {venue.capacity && (
        <p className="text-xs text-muted-foreground mt-0.5">
          Capacity: {venue.capacity.toLocaleString()}
        </p>
      )}

      {/* Description */}
      {venue.description && (
        <p className="text-sm text-muted-foreground text-center mt-3 max-w-md leading-relaxed">
          {venue.description}
        </p>
      )}

      {/* Social Links */}
      {hasSocialLinks && (
        <div className="flex flex-wrap justify-center gap-2.5 mt-5">
          {visibleSocialLinks.map((entry) => (
            <SocialIconLink key={entry.platform} platform={entry.platform} url={entry.url} />
          ))}
        </div>
      )}

      {/* Public contact tier, when the workspace published one */}
      <ProfileContact contact={venue.publicContact} />

      {/* Profile Blocks */}
      <div className="w-full space-y-3 mt-8">
        {customLinks.map((link) => {
          if (link.type === 'url') {
            return <ProfileLinkButton key={link.id} title={link.title} url={link.url} />;
          }
          if (link.type === 'upcoming_events') {
            return (
              <UpcomingEventsBlock
                key={link.id}
                link={link}
                events={venue.upcomingEvents}
              />
            );
          }
          return null;
        })}
      </div>

      {/* Who it played with (#41 phase 3) */}
      <div className="w-full mt-3">
        <PlayedTogether entityType="venue" entityId={venue.id} />
      </div>

    </div>
  );
};

export default VenueProfile;
